import {
  extractAppResources,
  extractRewardClaimTokens,
  extractLearnedTermsResource,
  extractStellineResource,
  extractStreakResource,
} from './appResources';
import { combineResourceRewardAlerts, deduplicateRewardAlerts } from './rewardAlertIdentity';
import { compareResourceOrder } from './resourceCelebrationTiming';

const ANONYMOUS_RESOURCE_ALERT_SUPPRESSION_WINDOW_MS = 15 * 60 * 1000;
const ANONYMOUS_RESOURCE_ALERT_SUPPRESSION_STORAGE_KEY = 'anonymous_resource_alert_suppression_v1';
const ANONYMOUS_SUPPRESSED_ALERT_TYPES = new Set(['gettoncini', 'stelline']);

const toPositiveNumber = (value) => {
  const numberValue = Number(value);
  if (Number.isFinite(numberValue) && numberValue > 0) {
    return numberValue;
  }
  return null;
};

const resolveAlertDelta = ({ isAnonymous, delta, potentialDelta }) => {
  if (delta != null) {
    return delta;
  }
  if (isAnonymous && potentialDelta != null) {
    return potentialDelta;
  }
  return null;
};

const buildAnonymousAlertFingerprint = (alert) => {
  if (!alert || typeof alert !== 'object') {
    return null;
  }

  if (alert.type === 'gettoncini') {
    const amount = Number(alert.amount);
    const normalizedAmount = Number.isFinite(amount) ? amount : 0;
    if (alert.rewardContributions?.length) return alert.rewardContributions.map((item) => item.id).join("|");
    const qualifier =
      alert.transactionKey || alert.message || alert.reason || `amount_${normalizedAmount}`;
    return `gettoncini:${normalizedAmount}:${qualifier}`;
  }

  if (alert.type === 'stelline') {
    const delta = Number(alert.delta);
    const normalizedDelta = Number.isFinite(delta) ? delta : 0;
    if (alert.rewardContributions?.length) return alert.rewardContributions.map((item) => item.id).join("|");
    const qualifier = alert.message || `delta_${normalizedDelta}`;
    return `stelline:${normalizedDelta}:${qualifier}`;
  }

  return null;
};

const loadAnonymousSuppressionState = () => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return {};
    }

    const rawState = window.localStorage.getItem(
      ANONYMOUS_RESOURCE_ALERT_SUPPRESSION_STORAGE_KEY,
    );
    if (!rawState) {
      return {};
    }

    const parsed = JSON.parse(rawState);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }
    return parsed;
  } catch {
    return {};
  }
};

const persistAnonymousSuppressionState = (state) => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }
    window.localStorage.setItem(
      ANONYMOUS_RESOURCE_ALERT_SUPPRESSION_STORAGE_KEY,
      JSON.stringify(state),
    );
  } catch {
    // Ignore storage failures; alerts should still render.
  }
};

const filterAnonymousSuppressedAlerts = ({
  alerts,
  isAnonymous,
  suppressionWindowMs = ANONYMOUS_RESOURCE_ALERT_SUPPRESSION_WINDOW_MS,
}) => {
  if (!isAnonymous || !Array.isArray(alerts) || alerts.length === 0) {
    return alerts;
  }

  const windowMs = Math.max(0, Number(suppressionWindowMs) || 0);
  if (windowMs <= 0) {
    return alerts;
  }

  const now = Date.now();
  const suppressionState = loadAnonymousSuppressionState();
  const nextState = {};

  Object.entries(suppressionState).forEach(([fingerprint, expiresAt]) => {
    const expiration = Number(expiresAt);
    if (Number.isFinite(expiration) && expiration > now) {
      nextState[fingerprint] = expiration;
    }
  });

  const filteredAlerts = [];
  alerts.forEach((alert) => {
    if (!ANONYMOUS_SUPPRESSED_ALERT_TYPES.has(alert.type)) {
      filteredAlerts.push(alert);
      return;
    }

    const fingerprint = buildAnonymousAlertFingerprint(alert);
    if (!fingerprint) {
      filteredAlerts.push(alert);
      return;
    }

    const existingExpiry = Number(nextState[fingerprint]);
    if (Number.isFinite(existingExpiry) && existingExpiry > now) {
      return;
    }

    nextState[fingerprint] = now + windowMs;
    filteredAlerts.push(alert);
  });

  persistAnonymousSuppressionState(nextState);
  return filteredAlerts;
};

export const buildResourceAlertsFromPayload = ({
  payload,
  isAnonymous = false,
  signedInDurationMs = 5000,
  anonymousDurationMs = 12000,
  includeStreakAlerts = false,
  suppressedGettonciniTransactionKeys = [],
}) => {
  if (!payload || typeof payload !== 'object') {
    return [];
  }

  const alerts = [];
  const durationMs = isAnonymous ? anonymousDurationMs : signedInDurationMs;
  const appResources = extractAppResources(payload);
  const rewardClaimTokens = extractRewardClaimTokens(payload);
  const suppressedTransactionKeys = new Set(
    Array.isArray(suppressedGettonciniTransactionKeys)
      ? suppressedGettonciniTransactionKeys.filter((key) => typeof key === 'string' && key.trim())
      : [],
  );

  if (Array.isArray(appResources?.metadata?.reward_events)) {
    const occurrenceAlerts = appResources.metadata.reward_events.flatMap((event) => {
      if (!event || typeof event.id !== 'string' || !event.id) return [];
      const delta = toPositiveNumber(event.delta);
      const potential = toPositiveNumber(event.potential_delta);
      const visibleDelta = resolveAlertDelta({ isAnonymous, delta, potentialDelta: potential });
      let alert;
      if (event.resource === 'streak') {
        if (!includeStreakAlerts) return [];
        const streak = toPositiveNumber(event.current_streak);
        if (streak == null) return [];
        alert = { type: 'streak', streak, message: event.message || null, duration: durationMs };
      } else if (['gettoncini', 'stelline', 'learned_terms'].includes(event.resource)) {
        if (visibleDelta == null) return [];
        if (event.resource === 'gettoncini' && suppressedTransactionKeys.has(event.transaction_key)) return [];
        alert = {
          type: event.resource,
          isAnonymous,
          message: event.message || null,
          duration: durationMs,
          ...(event.resource === 'gettoncini' ? {
            amount: visibleDelta,
            reason: event.message || event.description || '',
            transactionKey: event.transaction_key || null,
          } : {
            delta: visibleDelta,
            actualDelta: delta || 0,
            potentialDelta: potential || 0,
            rewardClaimTokens,
          }),
        };
      } else return [];
      return [{ ...alert, rewardContributions: [{ ...alert, id: event.id }] }];
    });
    return combineResourceRewardAlerts(deduplicateRewardAlerts(occurrenceAlerts))
      .sort(compareResourceOrder);
  }

  const streakResource = extractStreakResource(payload);
  const streakCurrent = toPositiveNumber(streakResource?.current_streak);
  if (includeStreakAlerts && streakResource?.streak_extended === true && streakCurrent != null) {
    alerts.push({
      type: 'streak',
      streak: streakCurrent,
      message: typeof streakResource?.message === 'string' ? streakResource.message : null,
      duration: durationMs,
    });
  }

  const stellineResource = extractStellineResource(payload);
  const stellineDelta = toPositiveNumber(stellineResource?.delta);
  const stellinePotentialDelta = toPositiveNumber(stellineResource?.potential_delta);
  const stellineAlertDelta = resolveAlertDelta({
    isAnonymous,
    delta: stellineDelta,
    potentialDelta: stellinePotentialDelta,
  });
  if (stellineAlertDelta != null) {
    alerts.push({
      type: 'stelline',
      delta: stellineAlertDelta,
      actualDelta: stellineDelta || 0,
      potentialDelta: stellinePotentialDelta || 0,
      isAnonymous,
      message:
        typeof stellineResource?.message === 'string'
          ? stellineResource.message
          : null,
      rewardClaimTokens,
      duration: durationMs,
    });
  }

  const learnedTermsResource = extractLearnedTermsResource(payload);
  const learnedTermsDelta = toPositiveNumber(learnedTermsResource?.delta);
  const learnedTermsPotentialDelta = toPositiveNumber(learnedTermsResource?.potential_delta);
  const learnedTermsAlertDelta = resolveAlertDelta({
    isAnonymous,
    delta: learnedTermsDelta,
    potentialDelta: learnedTermsPotentialDelta,
  });
  if (learnedTermsAlertDelta != null) {
    alerts.push({
      type: 'learned_terms',
      delta: learnedTermsAlertDelta,
      actualDelta: learnedTermsDelta || 0,
      potentialDelta: learnedTermsPotentialDelta || 0,
      isAnonymous,
      message:
        typeof learnedTermsResource?.message === 'string'
          ? learnedTermsResource.message
          : null,
      rewardClaimTokens,
      duration: durationMs,
    });
  }

  const gettonciniTransactions = Array.isArray(appResources?.gettoncini?.transactions)
    ? appResources.gettoncini.transactions
    : [];
  const seenTransactions = new Set();
  for (const transaction of gettonciniTransactions) {
    if (!transaction || typeof transaction !== 'object') continue;
    const fingerprint = transaction.reward_id || JSON.stringify([
      transaction.type, transaction.key, transaction.amount, transaction.description, transaction.message,
    ]);
    if (seenTransactions.has(fingerprint)) continue;
    seenTransactions.add(fingerprint);
    if (
      typeof transaction.key === 'string'
      && suppressedTransactionKeys.has(transaction.key)
    ) {
      continue;
    }
    const amount = toPositiveNumber(transaction.amount);
    if (amount == null) continue;

    const message = typeof transaction.message === 'string' ? transaction.message : null;
    const reason = message || transaction.description || '';
    alerts.push({
      type: 'gettoncini',
      amount,
      reason,
      message,
      transactionKey: transaction.key || null,
      ...(transaction.reward_id ? { rewardId: transaction.reward_id } : {}),
      isAnonymous,
      duration: durationMs,
    });
  }

  return alerts;
};

export const queueResourceAlertsFromPayload = ({
  queueAlert,
  payload,
  isAnonymous = false,
  firstAlertDelayMs = 0,
  signedInDurationMs = 5000,
  anonymousDurationMs = 12000,
  anonymousSuppressionWindowMs = ANONYMOUS_RESOURCE_ALERT_SUPPRESSION_WINDOW_MS,
  suppressedGettonciniTransactionKeys = [],
  excludedResourceTypes = [],
}) => {
  if (typeof queueAlert !== 'function') {
    return 0;
  }

  const alerts = buildResourceAlertsFromPayload({
    payload,
    isAnonymous,
    signedInDurationMs,
    anonymousDurationMs,
    suppressedGettonciniTransactionKeys,
  });
  if (isAnonymous) {
    return 0;
  }
  const visibleAlerts = filterAnonymousSuppressedAlerts({
    alerts: alerts.filter((alert) => !excludedResourceTypes.includes(alert.type)),
    isAnonymous,
    suppressionWindowMs: anonymousSuppressionWindowMs,
  });

  let acceptedCount = 0;
  visibleAlerts.forEach((alert) => {
    const accepted = queueAlert({
      ...alert,
      delay: acceptedCount === 0 ? Math.max(0, Number(firstAlertDelayMs) || 0) : 0,
    });
    // Existing callers return undefined; only an explicit false rejects an alert.
    if (accepted !== false) acceptedCount += 1;
  });
  return acceptedCount;
};
